// Traducción de video con lip-sync (HeyGen) — webhooks n8n
// `traducir_video_creator_start/status` migrados a atlas
// `/v1/videos/translate/start` y `/v1/videos/translate/status` (ADR-600).
// Flag ADENTRO del helper:
//   ON  → atlas con JWT: `user_id`/`company_id` NO viajan (salen del token);
//         `target_language` se sigue mandando (se ignora — siempre inglés).
//         Sin créditos en start = 402 tipado (n8n devolvía éxito sin jobId).
//   OFF → webhooks n8n byte-idénticos.
// Respuestas idénticas: start `{response:'success', jobId}`, status
// `{status:'running'|'failed'|'completed', path?, error?}` con el path
// relativo al bucket `video-project-assets`.

import {
  GENERATE_AUTH_TOKEN,
  TRANSLATE_VIDEO_START_URL,
  TRANSLATE_VIDEO_STATUS_URL,
  USE_ATLAS_BACKEND,
} from '../config';
import { atlasBackendFetch } from './atlasBackend';

// Mismo copy de saldo que las generaciones de imagen (generateAdRequest).
const BILLING_MESSAGE =
  'No te alcanzan los créditos para esta generación. Sumá créditos o actualizá tu plan para continuar.';

export interface TranslateStartBody {
  user_id: string;
  company_id: string;
  project_id: string;
  clip_id: string;
  video_url: string;
  duration_sec: number;
  target_language: string;
  modo_utilizado?: string;
}

/** Kickoff de la traducción. Devuelve el jobId o tira con mensaje mostrable. */
export async function postTranslateStart(
  body: TranslateStartBody,
): Promise<string> {
  let res: Response;
  if (USE_ATLAS_BACKEND) {
    const { user_id: _u, company_id: _c, ...atlasBody } = body;
    res = await atlasBackendFetch('/v1/videos/translate/start', atlasBody, {
      signal: AbortSignal.timeout(60_000),
    });
    if (res.status === 402) throw new Error(BILLING_MESSAGE);
  } else {
    res = await fetch(TRANSLATE_VIDEO_START_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(60_000),
    });
  }
  if (!res.ok) throw new Error(`Start failed: ${res.status}`);
  const json = (await res.json().catch(() => null)) as {
    response?: string;
    jobId?: string;
    error?: string;
  } | null;
  if (json?.response !== 'success' || !json?.jobId) {
    throw new Error(json?.error || 'El backend no devolvió un jobId válido.');
  }
  return json.jobId;
}

export interface TranslateStatusResult {
  status: 'running' | 'pending' | 'failed' | 'completed' | string;
  path?: string;
  error?: string;
}

/**
 * Un poll de status. Devuelve null en fallo transitorio (HTTP !ok / red) —
 * el caller sigue polleando, igual que hoy.
 */
export async function postTranslateStatus(body: {
  job_id: string;
  project_id: string;
  company_id: string;
}): Promise<TranslateStatusResult | null> {
  try {
    let res: Response;
    if (USE_ATLAS_BACKEND) {
      const { company_id: _c, ...atlasBody } = body;
      res = await atlasBackendFetch('/v1/videos/translate/status', atlasBody, {
        signal: AbortSignal.timeout(60_000),
      });
    } else {
      res = await fetch(TRANSLATE_VIDEO_STATUS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(60_000),
      });
    }
    if (!res.ok) return null;
    return (await res.json()) as TranslateStatusResult;
  } catch {
    return null;
  }
}
